import { useToast } from "@chakra-ui/react";
import { InfoIcon } from "@phosphor-icons/react";
import BaseImage from "@components/BaseImage";
import {
  LSTIconMapping,
  MAX_FRACTION_DIGITS,
  isNumber,
  toFixedWithoutRounding,
} from "@constants/common";
import useDappChain from "@hooks/useDappChain";
import useSwapTokensBalances from "@hooks/useSwapTokensBalances";
import {
  RSETH,
  swappingContractsConfig,
  WRSETH,
} from "@config/contractsConfig";
import { DEPRECATED_BRIDGE_CHAIN_IDS } from "@constants/chains";
import {
  APPROVE_WRAPPING_CLICK,
  CONNECT_WALLET,
  MAX_WRAP_CLICK,
  UNWRAP_INITIATED,
  UNWRAP_FAILED,
  UNWRAP_SUCCESSFUL,
  WRAP_INITIATED,
  WRAP_FAILED,
  WRAP_SUCCESSFUL,
  MAX_UNWRAP_CLICK,
  TOGGLE_WRAP_UNWRAP,
} from "@constants/analytics";
import {
  Button,
  Card,
  InputAmount,
  Typography,
} from "@kelp-dao/kernel-design-system";
import { CoinOption } from "@kelp-dao/kernel-design-system/dist/components/text-fields/InputAmount";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateUnwrapTxnState } from "@store/slices/globalSlice";
import {
  chainIconMapping,
  formatNumber,
  getUrlForLocalImage,
  isErrorRejectedByUser,
  trackEvent,
} from "utils";
import { formatUnits, parseEther, TransactionReceipt } from "viem";
import {
  useAccount,
  useBalance,
  usePublicClient,
  useWriteContract,
} from "wagmi";
import useLedgerCookies from "@hooks/useLedgerCookies";
import useSanction from "@hooks/useSanction";
import DepositModalV2, {
  TransactionType,
} from "@components/DepositModal/DepositModalV2";
import { TxnErrorType } from "@services/wagmi";
import { BW_VARIANT } from "@components/BridgeWrapV2/types";
import LearnMoreAccordianV2 from "@components/BridgeWrapV2/LearnMoreAccordianV2";

// Initial state for transaction modal
const initialTransactionState = {
  visible: false,
  complete: false,
  inProgress: false,
  type: TransactionType.APPROVAL,
  status: TransactionType.APPROVAL,
  error: {
    approval: null as TxnErrorType | null,
    deposit: null as TxnErrorType | null,
  },
  receipt: null as TransactionReceipt | null,
};

const WrapOnlyV2 = () => {
  useLedgerCookies();
  const { chain: wagmiChain, isConnected } = useAccount();
  const queryClient = useQueryClient();
  const { chain } = useDappChain();
  const client = usePublicClient();
  const dispatch = useDispatch();
  const { openConnectModal } = useConnectModal();
  const toast = useToast();
  const { isSanctioned } = useSanction();

  // State
  const [manualWrapMode, setManualWrapMode] = useState(true); // true = wrap (rsETH → wrsETH), false = unwrap (wrsETH → rsETH)
  const [swapAmount, setSwapAmount] = useState("");
  const [error, setError] = useState("");
  const [transaction, setTransaction] = useState(initialTransactionState);

  const { writeContract } = useWriteContract();

  const chainIdParam = chain?.id;

  // Get token config - don't require isConnected so UI shows properly
  const { rsETH = null, wrsETH = null } =
    chainIdParam && swappingContractsConfig[chainIdParam]?.tokens
      ? swappingContractsConfig[chainIdParam].tokens
      : {};

  // Deprecated-from-bridge chains keep wrsETH unwrapping only (no wrapping).
  // A chain qualifies when it has wrsETH/rsETH config AND is in the deprecation set.
  const isUnwrapOnly =
    chainIdParam != null &&
    !!rsETH &&
    !!wrsETH &&
    DEPRECATED_BRIDGE_CHAIN_IDS.has(chainIdParam);

  // Unwrap-only chains are always in unwrap mode; elsewhere honor the toggle.
  const isWrapMode = isUnwrapOnly ? false : manualWrapMode;

  const { data, queryKey } = useBalance({
    token: rsETH?.address,
    address: wrsETH?.address,
    chainId: chainIdParam,
    query: {
      refetchInterval: 10_000,
    },
  });

  const unwrapLimit =
    data && rsETH?.decimals
      ? Number(formatUnits(data.value, rsETH.decimals))
      : 0;

  const {
    rsETHBalance,
    rsETHAllowance,
    wrsETHBalance,
    wrsETHAllowance,
    invalidateCache,
  } = useSwapTokensBalances(chainIdParam || 1);

  const fromToken = isWrapMode ? rsETH : wrsETH;
  const toToken = isWrapMode ? wrsETH : rsETH;

  const fromTokenBal = formatUnits(
    isWrapMode ? rsETHBalance : wrsETHBalance,
    18
  );

  const toTokenBal = formatUnits(isWrapMode ? wrsETHBalance : rsETHBalance, 18);

  const tokenAllowance = formatUnits(
    isWrapMode ? rsETHAllowance : wrsETHAllowance,
    18
  );

  const hasSufficientAllowance =
    !isWrapMode || Number(tokenAllowance) >= Number(swapAmount);

  const hasBalance = Number(toFixedWithoutRounding(fromTokenBal, 4)) > 0;

  const checkInput = useCallback(
    (value = swapAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > Number(fromTokenBal)) {
        error = `You can't ${
          isWrapMode ? "wrap" : "unwrap"
        } more than available balance in your wallet.`;
      } else if (!isWrapMode && Number(value) > Number(unwrapLimit)) {
        error = `You can't unwrap more than available limit of ${unwrapLimit} ${fromToken?.symbol}.`;
      }
      return error;
    },
    [
      swapAmount,
      isConnected,
      fromTokenBal,
      isWrapMode,
      unwrapLimit,
      fromToken?.symbol,
    ]
  );

  useEffect(() => {
    if (swapAmount !== "") {
      const error = checkInput(swapAmount);
      setError(error);
    }
  }, [checkInput, swapAmount, fromTokenBal]);

  useEffect(() => {
    // Reset transaction state on chain/mode change
    setTransaction(initialTransactionState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, isWrapMode]);

  const handleApprove = () => {
    if (!client || !fromToken || !wrsETH) {
      return;
    }

    // Show modal with approval step
    setTransaction({
      ...initialTransactionState,
      visible: true,
      inProgress: true,
      type: TransactionType.APPROVAL,
      status: TransactionType.APPROVAL,
    });

    writeContract(
      {
        address: fromToken.address,
        abi: fromToken.abi,
        functionName: "approve",
        args: [wrsETH.address, parseEther(swapAmount)],
      },
      {
        onError: (error) => {
          trackEvent(APPROVE_WRAPPING_CLICK, {
            amount: swapAmount,
            network: chain?.name || "",
            chainId: String(chainIdParam),
            error: String(error),
            dapp: "kelp",
            location: "wrap_only_page",
          });
          setTransaction((prev) => ({
            ...prev,
            inProgress: false,
            error: {
              ...prev.error,
              approval: {
                promptError: error?.message ?? "",
                hasUserDenied: isErrorRejectedByUser(error),
              },
            },
          }));
        },
        onSuccess: async (hash) => {
          trackEvent(APPROVE_WRAPPING_CLICK, {
            amount: swapAmount,
            network: chain?.name || "",
            chainId: String(chainIdParam),
            rsETH_Balance: formatUnits(rsETHBalance, 18),
            wrsETH_Balance: formatUnits(wrsETHBalance, 18),
            dapp: "kelp",
            location: "wrap_only_page",
          });
          await client.waitForTransactionReceipt({ hash });
          invalidateCache();
          queryClient.invalidateQueries({ queryKey });
          // Move to step 2 and automatically proceed with wrap/unwrap
          setTransaction((transaction) => ({
            ...transaction,
            receipt: null,
            status: TransactionType.DEPOSIT,
            inProgress: false,
          }));
          // Automatically proceed with the transaction
          setTimeout(() => handleSwap(), 1000);
        },
      }
    );
  };

  const handleSwap = () => {
    if (!client || !fromToken || !rsETH || !wrsETH) {
      return;
    }
    const error = checkInput(swapAmount);
    if (error) {
      setError(error);
      return;
    }

    if (wagmiChain?.id !== chainIdParam) {
      toast({
        description:
          "Network and config mismatch. Please switch network first.",
        status: "error",
        duration: 5000,
        position: "top",
      });
      return;
    }

    // Update modal to show confirming state
    setTransaction((transaction) => ({
      ...transaction,
      inProgress: true,
      visible: true,
      status: TransactionType.DEPOSIT,
    }));

    const functionName = isWrapMode ? "deposit" : "withdraw";

    // Track wrap/unwrap_initiated event
    trackEvent(isWrapMode ? WRAP_INITIATED : UNWRAP_INITIATED, {
      network: chain?.name || "",
      source_token_symbol: fromToken?.symbol || "",
      destination_token_symbol: toToken?.symbol || "",
      amount: swapAmount,
      chain_id: String(chainIdParam),
      dapp: "kelp",
      location: "wrap_only_page",
    });

    writeContract(
      {
        address: wrsETH.address,
        abi: wrsETH.abi,
        functionName,
        args: [rsETH.address, parseEther(swapAmount)],
      },
      {
        onError: (error) => {
          setTransaction((prev) => ({
            ...prev,
            inProgress: false,
            complete: true,
            error: {
              ...prev.error,
              deposit: {
                promptError: error?.message ?? "",
                hasUserDenied: isErrorRejectedByUser(error),
              },
            },
          }));

          // Track wrap/unwrap_failed event
          trackEvent(isWrapMode ? WRAP_FAILED : UNWRAP_FAILED, {
            network: chain?.name || "",
            source_token_symbol: fromToken?.symbol || "",
            destination_token_symbol: toToken?.symbol || "",
            amount: swapAmount,
            chain_id: String(chainIdParam),
            error_reason: error?.message || String(error),
            dapp: "kelp",
            location: "wrap_only_page",
          });
        },
        onSuccess: async (hash) => {
          const receipt = await client.waitForTransactionReceipt({ hash });
          invalidateCache();
          queryClient.invalidateQueries({ queryKey });
          setSwapAmount("");

          if (!isWrapMode) {
            dispatch(
              updateUnwrapTxnState({
                success: true,
              })
            );
          }

          // Show success modal
          setTransaction((prev) => ({
            ...prev,
            inProgress: false,
            complete: true,
            receipt,
          }));

          // Track wrap/unwrap_successful event
          trackEvent(isWrapMode ? WRAP_SUCCESSFUL : UNWRAP_SUCCESSFUL, {
            network: chain?.name || "",
            source_token_symbol: fromToken?.symbol || "",
            destination_token_symbol: toToken?.symbol || "",
            amount: swapAmount,
            chain_id: String(chainIdParam),
            hash,
            dapp: "kelp",
            location: "wrap_only_page",
          });
        },
      }
    );
  };

  const handleMaxClick = () => {
    const ev = isWrapMode ? MAX_WRAP_CLICK : MAX_UNWRAP_CLICK;
    trackEvent(ev, {
      rsETH_Balance: formatUnits(rsETHBalance, 18),
      wrsETH_Balance: formatUnits(wrsETHBalance, 18),
      dapp: "kelp",
      location: "wrap_only_page",
    });
    setSwapAmount(toFixedWithoutRounding(fromTokenBal, 4));
  };

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setSwapAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setSwapAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleConnectWallet = () => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      location: "wrap_only_page",
      dapp: "kelp",
    });
  };

  const handleToggleMode = () => {
    setManualWrapMode((prev) => !prev);
    setSwapAmount("");
    setError("");
    setTransaction(initialTransactionState);
    trackEvent(TOGGLE_WRAP_UNWRAP, {
      location: "wrap_only_page",
      dapp: "kelp",
      selected: !isWrapMode,
      previousSelected: isWrapMode,
    });
  };

  const getButtonText = () => {
    if (!isConnected) return "Connect Wallet";
    if (!hasTokens) return "Unsupported Network";
    if (!hasSufficientAllowance)
      return "Approve & " + (isWrapMode ? "Wrap" : "Unwrap");
    if (isWrapMode) return "Confirm wrap";
    return "Confirm unwrap";
  };

  const handleButtonClick = () => {
    if (!isConnected) {
      handleConnectWallet();
    } else if (!hasTokens) {
      return;
    } else if (!hasSufficientAllowance) {
      handleApprove();
    } else {
      // Show modal and proceed to confirm step directly
      setTransaction({
        ...initialTransactionState,
        visible: true,
        inProgress: true,
        type: TransactionType.DEPOSIT,
        status: TransactionType.DEPOSIT,
      });
      handleSwap();
    }
  };

  // Handle confirm button in modal
  const handleConfirmInModal = () => {
    handleSwap();
  };

  // Close transaction modal
  const closeTransactionModal = () => {
    setTransaction(initialTransactionState);
  };

  // Retry approval
  const handleRetryApprove = () => {
    setTransaction((prev) => ({
      ...prev,
      error: { ...prev.error, approval: null },
    }));
    handleApprove();
  };

  const hasTokens = fromToken && toToken;

  return (
    <div
      className={`flex justify-center mx-auto w-full xl:w-[548px] lg:w-[568px] md:w-[592px] sm:w-[375px] ${
        isSanctioned
          ? "opacity-50 pointer-events-none"
          : "opacity-100 pointer-events-auto"
      }`}
    >
      <div className="flex flex-col gap-6 w-full">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <Typography
            as="h1"
            type="heading"
            className="w-full text-center md:text-left font-sans font-medium lg:font-semibold"
          >
            {isWrapMode ? "Wrap rsETH" : "Unwrap wrsETH"}
          </Typography>
          <Typography
            variant="xsmall"
            type="body"
            className="text-text-secondary w-full text-center md:text-left"
          >
            {isWrapMode
              ? "Convert rsETH ↔ wrapped rsETH on the same chain, safely and confidently."
              : "Convert wrapped rsETH ↔ rsETH on the same chain, safely and confidently."}
          </Typography>
        </div>

        {/* Main Card */}
        <Card variant="non-bordered" className="w-full p-4 md:p-8 rounded-2xl">
          <div className="flex flex-col gap-2">
            {isUnwrapOnly && (
              <div
                className="flex items-start gap-2 rounded-lg bg-[#F0F6FF] p-3"
                role="status"
              >
                <span className="shrink-0 mt-px inline-flex">
                  <InfoIcon size={18} weight="fill" color="#2F6FED" />
                </span>
                <Typography
                  variant="xsmall"
                  type="body"
                  className="text-text-primary"
                >
                  You can only unwrap wrsETH on{" "}
                  <span className="font-semibold">
                    {chain?.name || "this chain"}
                  </span>
                  . Wrapping is disabled here.
                </Typography>
              </div>
            )}

            {/* Network Selector */}

            {/* From Section */}
            <div className="flex flex-col gap-1 w-full">
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                From
              </Typography>
              <InputAmount
                key={`from-${isWrapMode ? RSETH : WRSETH}`}
                value={swapAmount}
                onChange={handleChange}
                placeholder="0"
                coins={[
                  {
                    id: isWrapMode ? RSETH : WRSETH,
                    label: isWrapMode ? RSETH : WRSETH,
                    iconSrc: getUrlForLocalImage(
                      LSTIconMapping[isWrapMode ? RSETH : WRSETH] || ""
                    ),
                  } as CoinOption,
                ]}
                coinSelected={{
                  id: isWrapMode ? RSETH : WRSETH,
                  label: isWrapMode ? RSETH : WRSETH,
                  iconSrc: getUrlForLocalImage(
                    LSTIconMapping[isWrapMode ? RSETH : WRSETH] || ""
                  ),
                }}
                showBalance={isConnected && !!hasTokens}
                coinBalance={
                  isConnected && hasTokens
                    ? formatNumber(Number(fromTokenBal), false, 4, true)
                    : "0"
                }
                rightButton={{
                  show: true,
                  text: "Max",
                  disabled: !isConnected || !hasTokens,
                  onClick: handleMaxClick,
                }}
                className="w-full"
              />
            </div>

            {/* Swap Toggle Button */}
            {!isUnwrapOnly && (
              <div className="flex justify-center mt-2.5">
                <button
                  onClick={handleToggleMode}
                  className="opacity-80 hover:opacity-100"
                  aria-label="Toggle wrap/unwrap"
                >
                  <BaseImage
                    src="/assets/swapV2.svg"
                    width={28}
                    height={28}
                    alt="swap"
                    className="w-7 h-7"
                  />
                </button>
              </div>
            )}

            {/* To Section */}
            <div className="flex flex-col gap-1 w-full mb-2.5">
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                To
              </Typography>
              <InputAmount
                key={`to-${isWrapMode ? WRSETH : RSETH}`}
                value={swapAmount}
                onChange={() => {}}
                disabled={true}
                placeholder="0"
                coins={[
                  {
                    id: isWrapMode ? WRSETH : RSETH,
                    label: isWrapMode ? WRSETH : RSETH,
                    iconSrc: getUrlForLocalImage(
                      LSTIconMapping[isWrapMode ? WRSETH : RSETH] || ""
                    ),
                  } as CoinOption,
                ]}
                coinSelected={{
                  id: isWrapMode ? WRSETH : RSETH,
                  label: isWrapMode ? WRSETH : RSETH,
                  iconSrc: getUrlForLocalImage(
                    LSTIconMapping[isWrapMode ? WRSETH : RSETH] || ""
                  ),
                }}
                showBalance={isConnected && !!hasTokens}
                coinBalance={
                  isConnected && hasTokens
                    ? formatNumber(Number(toTokenBal), false, 4, true)
                    : "0"
                }
                rightButton={{ show: false }}
                className="w-full"
                inputStyle={{
                  color: "#1a1a1a",
                }}
              />
            </div>

            {/* Unwrap Limit */}
            {!isWrapMode && hasTokens && (
              <div className="flex justify-between gap-1 w-full">
                <Typography
                  variant="small"
                  type="body"
                  className="text-text-secondary"
                >
                  Unwrap limit
                </Typography>
                <Typography
                  variant="small"
                  type="body"
                  className="text-text-secondary"
                >
                  {+unwrapLimit.toFixed(4)} wrsETH
                </Typography>
              </div>
            )}

            {chain?.id && chain?.name && (
              <div className="flex items-center justify-between">
                <Typography
                  variant="small"
                  type="body"
                  className="text-text-secondary"
                >
                  Selected network
                </Typography>
                <div className="flex items-center gap-1 rounded-lg border-border-default">
                  <BaseImage
                    src={chainIconMapping[chain?.id]}
                    width={20}
                    height={20}
                    alt={chain?.name || "Ethereum"}
                    className="rounded-full"
                  />
                  <Typography
                    variant="small"
                    type="body"
                    className="text-text-secondary"
                  >
                    {chain?.name}
                  </Typography>
                </div>
              </div>
            )}

            {/* Action Button */}
            <Button
              variant="primary"
              className="w-full py-4 mt-3"
              disabled={!!error || !swapAmount || !isConnected || !hasTokens}
              onClick={handleButtonClick}
            >
              {getButtonText()}
            </Button>

            {/* Error Messages */}
            {error && (
              <div className="flex justify-center items-center gap-1">
                <BaseImage
                  src="/assets/transactions/error.svg"
                  width={16}
                  height={16}
                  alt="error-icon"
                />
                <Typography
                  variant="xsmall"
                  type="body"
                  className="text-feedback-error"
                >
                  {error}
                </Typography>
              </div>
            )}

            {!hasBalance && !error && isConnected && hasTokens && (
              <div className="flex justify-center items-center gap-1">
                <BaseImage
                  src="/assets/transactions/error.svg"
                  width={16}
                  height={16}
                  alt="error-icon"
                />
                <Typography
                  variant="xsmall"
                  type="body"
                  className="text-feedback-error"
                >
                  Not enough balance for this transaction
                </Typography>
              </div>
            )}
          </div>
        </Card>

        {/* Learn More Section */}
        <LearnMoreAccordianV2 variant={BW_VARIANT.RSETH} />
      </div>

      {/* Transaction Modal */}
      {transaction.visible && (
        <DepositModalV2
          transaction={{
            from: fromToken?.symbol!,
            to: toToken?.symbol!,
            amountIn: swapAmount,
            amountOut: swapAmount,
          }}
          complete={transaction.complete}
          inProgress={transaction.inProgress}
          type={transaction.type}
          error={transaction.error}
          visible={transaction.visible}
          status={transaction.status}
          flags={{
            isVault: false,
            isUnstake: false,
            isWrap: isWrapMode,
          }}
          closeAlert={closeTransactionModal}
          retryTransaction={handleRetryApprove}
          handleConfirm={handleConfirmInModal}
          receipt={transaction.receipt}
          txModalTitle={
            <Typography variant="small" type="body" className="font-medium">
              Review {isWrapMode ? "wrap" : "unwrap"}
            </Typography>
          }
        />
      )}
    </div>
  );
};

export default WrapOnlyV2;
